import { useOpenConfirmModal } from '@/pages/cvc/hooks/useConfirmModal';
import { listFailedNames } from '@/util/failureList';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * The user-facing voice for the two request failures that previously had none:
 * `POST /resources` (an upload that never starts) and `POST /cvc` `/mss` (a
 * generation that never starts), plus the "no session yet" case that can cause
 * either of them.
 *
 * All three go through the app's acknowledgement channel — `openConfirmModal`
 * with `hideCancel`, the ISSUE-009 precedent — rather than a new surface,
 * because there is nothing for the user to decide: the action did not happen
 * and the only response is to acknowledge it. Batch failures list at most
 * `MAX_LISTED_FAILURES` names and get ONE modal for the whole run, matching
 * `useDownload`.
 *
 * The copy lives here and nowhere else. Both callers (`useUploadSlots`,
 * `useJobSubmit`) can raise the not-connected notice, so keeping its i18n key
 * in one module is what stops the two from drifting into two different
 * sentences for the same condition.
 */
const useSessionNotice = () => {
  // The write-only accessor, deliberately: this hook never renders the modal,
  // and the reading form would subscribe every panel that raises a notice to
  // every modal open and close in the app.
  const openConfirmModal = useOpenConfirmModal();
  const { t } = useTranslation();

  const acknowledge = useCallback(
    (message: string) => {
      openConfirmModal({
        message,
        confirmText: t('OK') as string,
        hideCancel: true,
      });
    },
    [openConfirmModal, t]
  );

  /**
   * The WebSocket session id is not available yet, so the request cannot
   * identify itself to the backend. Deliberately phrased as "wait and retry"
   * rather than as an error: on a normal page load this window is short, and
   * a genuine outage is already announced separately by the header's live
   * region ("Live updates unavailable. Reconnecting...", ISSUE-022).
   */
  const notifyNotConnected = useCallback(() => {
    acknowledge(
      t('Not connected to the server yet. Please wait a moment and try again.')
    );
  }, [acknowledge, t]);

  /**
   * The backend refused to create a resource for these files, so they have no
   * row and no FAILED badge to carry the news — this modal is their only
   * surface. Named files, because a partial refusal is now possible and the
   * user otherwise cannot tell which of their files got through.
   */
  const notifyUploadRefused = useCallback(
    (failedNames: string[]) => {
      if (!failedNames.length) return;
      const { names, more } = listFailedNames(failedNames);
      acknowledge(
        more
          ? t('Could not start uploading: {{names}} and {{more}} more', {
              names,
              more,
            })
          : t('Could not start uploading: {{names}}', { names })
      );
    },
    [acknowledge, t]
  );

  /**
   * The generation request itself failed, so no job ids came back and no
   * result rows were created. Unlike an upload there are no per-file names to
   * report: the request covers the whole selection and fails as one.
   */
  const notifyGenerateFailed = useCallback(() => {
    acknowledge(t('Could not start generating. Please try again.'));
  }, [acknowledge, t]);

  return useMemo(
    () => ({ notifyNotConnected, notifyUploadRefused, notifyGenerateFailed }),
    [notifyNotConnected, notifyUploadRefused, notifyGenerateFailed]
  );
};

export default useSessionNotice;
